import React from 'react'
import { TouchableOpacity, View, Text, StyleSheet } from 'react-native'
import { Icon, IconName } from '../../../components/atoms'
import { Theme } from '@theme'
import { Scope } from '@utils'
import { useTheme } from '@react-navigation/native'
import { useLanguage } from '@context'

const getActiveScopeColor = (scope: Scope, theme: Theme) => {
  return scope === Scope.PRIVATE
    ? theme.colors.green
    : scope === Scope.PROFESSIONAL
      ? theme.colors.primary
      : scope === Scope.GROUP
        ? theme.colors.pink
        : scope === Scope.GHOST
          ? theme.colors.purple
          : theme.colors.greyBlue
}

const ContactsFilter = ({
  scope = [],
  noGhost = false,
  noGroup = true,
  allLabel,
  onScopeChange,
}: {
  scope: Scope[]
  noGhost?: boolean
  noGroup?: boolean
  // When set, a first chip with this label shows "no filter": it is active
  // while no other chip is, and pressing it clears the selection.
  allLabel?: string
  onScopeChange: (scope: Scope[]) => void
}) => {
  const theme = useTheme()
  const { t } = useLanguage()
  const styles = getStyle(theme)

  const onToggleScope = (scopeType: Scope) => {
    let newScope = scope
    // If toggling PRIVATE or PROFESSIONAL, remove the other if present
    if (scopeType === Scope.PRIVATE) {
      if (scope.includes(Scope.PRIVATE)) {
        // Remove PRIVATE
        newScope = scope.filter((s) => s !== Scope.PRIVATE)
      } else {
        // Add PRIVATE, remove PROFESSIONAL if present
        newScope = [...scope.filter((s) => s !== Scope.PROFESSIONAL), Scope.PRIVATE]
      }
    } else if (scopeType === Scope.PROFESSIONAL) {
      if (scope.includes(Scope.PROFESSIONAL)) {
        // Remove PROFESSIONAL
        newScope = scope.filter((s) => s !== Scope.PROFESSIONAL)
      } else {
        // Add PROFESSIONAL, remove PRIVATE if present
        newScope = [...scope.filter((s) => s !== Scope.PRIVATE), Scope.PROFESSIONAL]
      }
    } else if (scopeType === Scope.GROUP) {
      if (scope.includes(Scope.GROUP)) {
        newScope = scope.filter((s) => s !== Scope.GROUP)
      } else {
        newScope = [...scope, Scope.GROUP]
      }
    } else if (scopeType === Scope.GHOST) {
      // For all other scopes, toggle as usual
      newScope = scope.includes(scopeType)
        ? scope.filter((s) => s !== scopeType)
        : [...scope, scopeType]
    }
    onScopeChange(newScope)
  }

  const isAllActive = scope.length === 0

  return (
    <View style={styles.filtersContainer}>
      {!!allLabel && (
        <TouchableOpacity
          testID="scope-all"
          style={[styles.filterButton, isAllActive && { borderColor: theme.colors.primary }]}
          onPress={() => onScopeChange([])}
        >
          <Text
            style={[
              styles.filterText,
              styles.allText,
              isAllActive && { fontWeight: 'bold', color: theme.colors.primary },
            ]}
          >
            {allLabel}
          </Text>
        </TouchableOpacity>
      )}
      {[
        { scope: Scope.PRIVATE, icon: 'lock' as IconName, name: t('common.private') },
        { scope: Scope.PROFESSIONAL, icon: 'work' as IconName, name: t('common.pro') },
        { scope: Scope.GROUP, icon: 'people' as IconName, name: t('common.group') },
        { scope: Scope.GHOST, icon: 'ghost' as IconName, name: 'Ghost' },
      ]
        .filter((item) => !noGhost || item.scope !== Scope.GHOST)
        .filter((item) => !noGroup || item.scope !== Scope.GROUP)
        .map((item: { scope: Scope; icon: IconName; name: string }) => {
          const isActive = scope.includes(item.scope)
          const activeColor = getActiveScopeColor(item.scope, theme)
          return (
            <TouchableOpacity
              key={item.scope}
              testID={`scope-${item.scope.toLowerCase()}`}
              // The ghost chip is an icon alone: give it a name to announce.
              accessibilityLabel={
                item.scope === Scope.GHOST ? t('graphScreen.legendGhost') : undefined
              }
              style={[
                styles.filterButton,
                isActive && { /*backgroundColor: activeColor + 20, */ borderColor: activeColor },
              ]}
              onPress={() => onToggleScope(item.scope)}
            >
              <Icon
                name={item.icon}
                size={16}
                color={isActive ? activeColor : theme.colors.greyBlue}
              />
              {item.scope !== Scope.GHOST && (
                <Text
                  style={[
                    styles.filterText,
                    isActive && { fontWeight: 'bold', color: activeColor },
                  ]}
                >
                  {item.name}
                </Text>
              )}
            </TouchableOpacity>
          )
        })}
    </View>
  )
}

const getStyle = (theme: Theme) =>
  StyleSheet.create({
    filtersContainer: {
      flexDirection: 'row',
    },
    filterButton: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      paddingHorizontal: 12,
      height: 36,
      borderRadius: 20,
      borderWidth: 1,
      borderColor: theme.colors.lightGrey,
      marginRight: 8,
      backgroundColor: 'transparent',
    },
    filterText: {
      fontSize: 14,
      color: theme.colors.greyBlue,
      marginLeft: 4,
    },
    allText: {
      marginLeft: 0,
    },
  })

export default ContactsFilter
